import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, ShoppingCart, Clock, X, ArrowRight, CheckCircle2 } from "lucide-react";
import { api } from "../lib/api";

export default function CheckoutUpsell() {
  const [upsells, setUpsells] = useState([]);
  const [loading, setLoading] = useState(true);
  const [accepted, setAccepted] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    let alive = true;
    api
      .get("/upsells/active")
      .then((r) => {
        if (!alive) return;
        const list = r.data.upsells || [];
        setUpsells(list);
        // Fire view-event for every offer rendered
        list.forEach((u) => api.post("/upsells/event", { upsell_id: u.id, action: "view" }).catch(() => {}));
      })
      .catch((e) => console.error("upsells load", e))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  const accept = (u) => {
    api.post("/upsells/event", { upsell_id: u.id, action: "accept" }).catch(() => {});
    setAccepted(u);
  };

  const skip = () => {
    upsells.forEach((u) =>
      api.post("/upsells/event", { upsell_id: u.id, action: "skip" }).catch(() => {})
    );
    navigate("/dashboard");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-fc-navy text-white flex items-center justify-center">
        <div className="w-12 h-12 border-2 border-fc-red border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!upsells.length) {
    return (
      <div className="min-h-screen bg-fc-navy text-white flex items-center justify-center px-6" data-testid="checkout-upsell-empty">
        <div className="text-center">
          <CheckCircle2 className="mx-auto text-[#10B981] mb-4" size={48} />
          <h1 className="fc-h-section text-2xl mb-2">You're all set.</h1>
          <p className="text-white/65 text-sm mb-6">No add-ons available right now.</p>
          <button onClick={() => navigate("/dashboard")} className="btn-fc-red" data-testid="upsell-go-dashboard">
            Continue to Dashboard <ArrowRight size={14} />
          </button>
        </div>
      </div>
    );
  }

  if (accepted) {
    return (
      <div className="min-h-screen bg-fc-navy text-white flex items-center justify-center px-6" data-testid="upsell-accepted-page">
        <div className="max-w-md w-full bg-[#060A24] border border-white/10 p-10 text-center">
          <CheckCircle2 className="mx-auto text-[#10B981] mb-4" size={48} />
          <h1 className="fc-h-section text-2xl mb-3">Thanks — request logged.</h1>
          <p className="text-white/65 text-sm mb-6">
            Our team will reach out shortly with payment + access details for{" "}
            <b className="text-white">{accepted.name}</b>.
          </p>
          <button onClick={() => navigate("/dashboard")} className="btn-fc-red w-full justify-center" data-testid="upsell-continue-dashboard">
            <Sparkles size={14} /> Continue to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-fc-navy text-white" data-testid="checkout-upsell-page">
      <header className="border-b border-white/10 px-6 lg:px-10 py-5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src="/brand/freddy-symbol.webp" alt="Freddy" className="h-8 w-auto" />
          <p className="fc-overline text-[10px] tracking-[0.22em] text-white/55">SPECIAL OFFER · ONE-TIME</p>
        </div>
        <button
          onClick={skip}
          className="text-[11px] tracking-[0.22em] uppercase font-bold text-white/45 hover:text-fc-red flex items-center gap-1"
          data-testid="upsell-skip"
        >
          NO THANKS, CONTINUE <X size={12} />
        </button>
      </header>

      <main className="max-w-5xl mx-auto px-6 lg:px-10 py-10 lg:py-14">
        <div className="text-center mb-10">
          <p className="fc-overline text-fc-red text-[11px] mb-2">YOU'RE IN — NOW TURBOCHARGE IT</p>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight uppercase mb-4">
            Add these before you start
          </h1>
          <p className="text-white/65 text-sm sm:text-base max-w-xl mx-auto">
            Exclusive bonuses available only to new members — claim now or never.
          </p>
        </div>

        <div className={`grid gap-6 ${upsells.length === 1 ? "max-w-md mx-auto" : "lg:grid-cols-2"}`}>
          {upsells.map((u) => {
            const hasDiscount = u.discount_price_cents && u.discount_price_cents > 0;
            return (
              <article
                key={u.id}
                className="bg-[#060A24] border border-fc-red/30 p-7 flex flex-col"
                data-testid={`upsell-card-${u.id}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <span className="text-[10px] tracking-[0.22em] uppercase font-black text-fc-red flex items-center gap-1">
                    <ShoppingCart size={11} /> {u.type.replace(/_/g, " ")}
                  </span>
                  {u.urgency_message && (
                    <span className="text-[10px] tracking-wider uppercase font-bold text-[#F59E0B] flex items-center gap-1">
                      <Clock size={11} /> {u.urgency_message}
                    </span>
                  )}
                </div>

                <h2 className="text-2xl font-black tracking-tight mb-2">{u.name}</h2>
                <p className="text-white/70 text-sm mb-5">{u.description}</p>

                {(u.features || []).length > 0 && (
                  <ul className="text-xs text-white/65 space-y-1.5 mb-6">
                    {u.features.map((f, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <CheckCircle2 size={13} className="text-[#10B981] flex-shrink-0 mt-0.5" /> {f}
                      </li>
                    ))}
                  </ul>
                )}

                <div className="mt-auto">
                  <p className="mb-4">
                    {hasDiscount ? (
                      <>
                        <span className="text-fc-red text-3xl font-black">${(u.discount_price_cents / 100).toFixed(0)}</span>
                        <span className="ml-2 text-white/40 text-base line-through">${(u.price_cents / 100).toFixed(0)}</span>
                      </>
                    ) : (
                      <span className="text-fc-red text-3xl font-black">${(u.price_cents / 100).toFixed(0)}</span>
                    )}
                  </p>
                  <button
                    onClick={() => accept(u)}
                    className="btn-fc-red w-full justify-center"
                    data-testid={`upsell-accept-${u.id}`}
                  >
                    <Sparkles size={14} /> YES, ADD THIS
                  </button>
                </div>
              </article>
            );
          })}
        </div>

        <div className="text-center mt-10">
          <button
            onClick={skip}
            className="text-xs tracking-[0.18em] uppercase font-bold text-white/45 hover:text-white"
            data-testid="upsell-skip-bottom"
          >
            No thanks, continue to dashboard →
          </button>
        </div>
      </main>
    </div>
  );
}
